/**
 * KpiStyle — Chart style section for KPI cards.
 *
 * Controls sparkline appearance: show/hide, line style, thickness,
 * area opacity, and markers — mirroring Line/Area controls.
 */
import React from "react";
import { Checkbox, Field, Label, Radio, RadioGroup, Slider } from "@fluentui/react-components";
import type { LineStyleOption } from "../visualConfig";
import { type ChartStyleSectionProps, useSliderStyles } from "./shared";

const KpiStyle: React.FC<ChartStyleSectionProps> = ({ chartStyle: cs, onStyleChange }) => {
  const styles = useSliderStyles();
  const showSparkline = cs.showSparkline ?? true;

  return (
    <>
      {/* ── Show Comparison Toggle ─────────────────────────── */}
      <Checkbox
        label="Show previous period comparison"
        checked={cs.showComparison ?? true}
        onChange={(_, data) => onStyleChange({ showComparison: !!data.checked })}
      />

      {/* ── Show Sparkline Toggle ──────────────────────────── */}
      <Checkbox
        label="Show sparkline"
        checked={showSparkline}
        onChange={(_, data) => onStyleChange({ showSparkline: !!data.checked })}
      />

      {showSparkline && (
        <>
          {/* ── Sparkline Position ─────────────────────────── */}
          <Field label="Sparkline Position">
            <RadioGroup
              layout="horizontal"
              value={cs.sparklinePosition ?? "right"}
              onChange={(_, data) =>
                onStyleChange({ sparklinePosition: data.value as 'bottom' | 'top' | 'left' | 'right' })
              }
            >
              <Radio value="right" label="Right" />
              <Radio value="bottom" label="Bottom" />
              <Radio value="left" label="Left" />
              <Radio value="top" label="Top" />
            </RadioGroup>
          </Field>

          {/* ── Line Style ──────────────────────────────────── */}
          <Field label="Line Style">
            <RadioGroup
              layout="horizontal"
              value={cs.lineStyle ?? "smooth"}
              onChange={(_, data) =>
                onStyleChange({ lineStyle: (data.value as LineStyleOption) ?? "smooth" })
              }
            >
              <Radio value="regular" label="Regular" />
              <Radio value="smooth" label="Smooth" />
              <Radio value="step" label="Step" />
            </RadioGroup>
          </Field>

          {/* ── Line Width ──────────────────────────────────── */}
          <div className={styles.sliderField}>
            <Label size="small">Line Width</Label>
            <div className={styles.sliderRow}>
              <Slider
                min={1}
                max={6}
                step={0.5}
                value={cs.lineWidth ?? 2}
                onChange={(_, data) => onStyleChange({ lineWidth: data.value })}
                style={{ flex: 1 }}
              />
              <span className={styles.sliderValue}>{cs.lineWidth ?? 2}px</span>
            </div>
          </div>

          {/* ── Area Opacity ────────────────────────────────── */}
          <div className={styles.sliderField}>
            <Label size="small">Area Opacity</Label>
            <div className={styles.sliderRow}>
              <Slider
                min={0}
                max={100}
                step={5}
                value={Math.round((cs.areaOpacity ?? 0.2) * 100)}
                onChange={(_, data) => onStyleChange({ areaOpacity: data.value / 100 })}
                style={{ flex: 1 }}
              />
              <span className={styles.sliderValue}>
                {Math.round((cs.areaOpacity ?? 0.2) * 100)}%
              </span>
            </div>
          </div>

          {/* ── Markers ─────────────────────────────────────── */}
          <Checkbox
            label="Show markers"
            checked={cs.showMarkers ?? false}
            onChange={(_, data) => onStyleChange({ showMarkers: !!data.checked })}
          />
          {(cs.showMarkers ?? false) && (
            <div className={styles.sliderField}>
              <Label size="small">Marker Size</Label>
              <div className={styles.sliderRow}>
                <Slider
                  min={2}
                  max={12}
                  step={1}
                  value={cs.markerSize ?? 3}
                  onChange={(_, data) => onStyleChange({ markerSize: data.value })}
                  style={{ flex: 1 }}
                />
                <span className={styles.sliderValue}>{cs.markerSize ?? 3}px</span>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default KpiStyle;
